import React, { useMemo, useState } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import axios from "axios";
import FailedMsg from "../../../../Extra/FailedMsg";
import SuccessMsg from "../../../../Extra/SuccessMsg";
import { MdDeleteForever } from "react-icons/md";
const urlPrefix = process.env.REACT_APP_URL_PREFIX;

const HistoryList = ({
  caseData,
  historyData = [],
  documents = [],
  remindersData = [],
  onEdit,
  onReminder,
  fetchHistory,
}) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  // ============================================================
  // PDF VIEWER STATE
  // ============================================================

  const [selectedPdf, setSelectedPdf] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [show, setShow] = useState(false);
  const [result, setResult] = useState({
    success: "",
    error: "",
  });
  // ============================================================
  // SORT
  // Latest created_at first
  // ============================================================

  const sortedHistory = useMemo(() => {
    return [...historyData].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();

      return dateB - dateA;
    });
  }, [historyData, result]);
  const topHistory = sortedHistory[0];

  // ============================================================
  // CAN EDIT
  // ============================================================

  const canEdit = (history, index) => {
    if (!history || index !== 0 || !history.created_at) {
      return false;
    }

    /*
      Server is sending IST time but incorrectly appending Z.

      Example:
      2026-10-01T10:42:30.000Z

      Actually means:
      2026-10-01 10:42:30 IST
    */

    const serverDateString = history.created_at.replace("Z", "+05:30");

    const createdAt = new Date(serverDateString);
    const now = new Date();

    const difference = now.getTime() - createdAt.getTime();

    const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;

    return difference >= 0 && difference <= THREE_DAYS;
  };

  // ============================================================
  // GET REMINDERS FOR HISTORY
  // ============================================================

  const getReminders = (history) => {
    if (!history || !history.status_stage) {
      return [];
    }

    return remindersData.filter((reminder) => {
      return (
        String(reminder?.office_or_stage || "")
          .trim()
          .toUpperCase() ===
        String(history?.status_stage || "")
          .trim()
          .toUpperCase()
      );
    });
  };

  // ============================================================
  // GET DOCUMENTS FOR HISTORY
  // ============================================================

  const getDocuments = (history) => {
    if (!history?.history_id) {
      return [];
    }

    return documents.filter(
      (document) => String(document?.history_id) === String(history.history_id),
    );
  };

  // ============================================================
  // OPEN PDF
  // ============================================================

  const openPdf = (document) => {
    if (!document?.download_url) {
      return;
    }

    setSelectedPdf(document);
    setShowPdfViewer(true);
  };

  // ============================================================
  // CLOSE PDF
  // ============================================================

  const closePdf = () => {
    setShowPdfViewer(false);
    setSelectedPdf(null);
  };

  // ============================================================
  // FORMAT DATE
  // ============================================================

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    return String(date).slice(0, 10);
  };

  // ============================================================
  // FORMAT DATE TIME
  // ============================================================

  const formatDateTime = (date) => {
    if (!date) {
      return "-";
    }

    /*
      Backend timestamp is actually IST but has Z.
      Replace Z with +05:30 before parsing.
    */

    let parsedDate = date;

    if (typeof date === "string" && date.endsWith("Z")) {
      parsedDate = date.replace("Z", "+05:30");
    }

    const d = new Date(parsedDate);

    if (Number.isNaN(d.getTime())) {
      return "-";
    }

    return d.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Kolkata",
    });
  };

  // ============================================================
  // AGE
  // ============================================================

  const getAgeText = (date) => {
    if (!date) {
      return "";
    }

    let parsedDate = date;

    if (typeof date === "string" && date.endsWith("Z")) {
      parsedDate = date.replace("Z", "+05:30");
    }

    const createdAt = new Date(parsedDate);

    if (Number.isNaN(createdAt.getTime())) {
      return "";
    }

    const difference = Date.now() - createdAt.getTime();

    if (difference < 0) {
      return "Just now";
    }

    const hours = Math.floor(difference / (1000 * 60 * 60));

    if (hours < 1) {
      return "Less than 1 hour old";
    }

    if (hours < 24) {
      return `${hours} hour${hours === 1 ? "" : "s"} old`;
    }

    const days = Math.floor(hours / 24);

    return `${days} day${days === 1 ? "" : "s"} old`;
  };

  // ============================================================
  // EMPTY
  // ============================================================

  if (sortedHistory.length === 0) {
    return (
      <div>
        <h6 className="fw-bold border-bottom pb-2 mb-3">Previous History</h6>

        <div className="text-center text-muted py-4">
          No previous history found.
        </div>
      </div>
    );
  }

  const HandleDelete = async (from, data) => {
    setShow(false);

    try {
      const conf = window.confirm(`Are you sure you wan to delete ${from}`);
      if (!conf) return;
      let response;
      if (from === "reminder") {
        response = await axios.delete(
          `${urlPrefix}store/procurements/reminders/${data.reminder_id}`,
        );
      } else {
        response = await axios.delete(
          `${urlPrefix}store/procurements/history/${data.history_id}`,
        );
      }
      setShow(true);
      setResult({
        success: true,
        message: response.data.message,
      });
      fetchHistory();
    } catch (err) {
      setShow(true);
      setResult({
        success: false,
        message: err?.message || "error",
      });
    }
  };
  // ============================================================
  // RENDER
  // ============================================================
  return (
    <>
      <div>
        {/* ======================================================
            HEADER
        ====================================================== */}

        <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
          <h6 className="fw-bold mb-0">Previous History</h6>

          {topHistory && (
            <small className="text-muted">
              Latest: {getAgeText(topHistory.created_at)}
            </small>
          )}
        </div>

        {/* ======================================================
            HISTORY
        ====================================================== */}

        <div className="d-flex flex-column gap-3">
          {sortedHistory.map((history, index) => {
            const isLatest = index === 0;

            const editable = canEdit(history, index);

            const reminders = getReminders(history);

            const historyDocuments = getDocuments(history);

            return (
              <div
                key={history.history_id || index}
                className={`border rounded p-3 ${
                  isLatest ? "border-primary" : ""
                }`}
              >
                {/* ==================================================
                    HEADER
                ================================================== */}

                <div className="d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <strong>{history.status_stage}</strong>

                      {isLatest && (
                        <span className="badge bg-primary">Latest</span>
                      )}
                    </div>

                    <div className="small text-muted mt-1">
                      Action Date: {formatDate(history.action_date)}
                    </div>

                    <div className="small text-muted">
                      Created: {formatDateTime(history.created_at)}
                    </div>

                    <div className="small text-muted">
                      Created By: {history.created_by || "-"}
                    </div>
                  </div>

                  {/* ==================================================
                      ACTION BUTTONS
                  ================================================== */}

                  {isLatest && (
                    <div className="d-flex gap-2 flex-wrap justify-content-end">
                      {editable && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => onEdit(history)}
                        >
                          ✏️ Edit
                        </button>
                      )}

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => onReminder(history)}
                      >
                        🔔 Reminder
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => HandleDelete("history", history)}
                      >
                        <MdDeleteForever />
                      </button>
                    </div>
                  )}
                </div>

                {/* ==================================================
                    DOCUMENTS
                ================================================== */}

                {historyDocuments.length > 0 && (
                  <div className="mt-3">
                    <div className="small fw-bold text-primary mb-2">
                      📎 Documents
                    </div>

                    <div className="d-flex flex-column gap-2">
                      {historyDocuments.map((document, documentIndex) => {
                        const isPdf =
                          document?.mime_type === "application/pdf" ||
                          String(document?.original_file_name || "")
                            .toLowerCase()
                            .endsWith(".pdf");

                        return (
                          <div
                            key={document.document_id || documentIndex}
                            className="border rounded p-2 d-flex justify-content-between align-items-center gap-3"
                          >
                            <div className="d-flex align-items-center gap-2">
                              <span className="fs-5">
                                {isPdf ? "📄" : "📎"}
                              </span>

                              <div>
                                <div className="fw-semibold">
                                  {document.original_file_name || "Document"}
                                </div>

                                <div className="small text-muted">
                                  {document.document_type || "-"}
                                  {document.file_size
                                    ? ` • ${(
                                        Number(document.file_size) / 1024
                                      ).toFixed(1)} KB`
                                    : ""}
                                </div>
                              </div>
                            </div>

                            {/* PDF VIEW BUTTON */}

                            {isPdf && document.download_url && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => openPdf(document)}
                              >
                                📄 View PDF
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ==================================================
                    REMINDERS
                ================================================== */}

                {reminders.length > 0 && (
                  <div className="mt-3">
                    <div className="small fw-bold text-warning-emphasis mb-2">
                      🔔 Reminders
                    </div>

                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-0 align-middle">
                        <thead className="table-warning">
                          <tr>
                            <th>Stage</th>
                            <th>Reminder Date</th>
                            <th>Remarks</th>
                            {/* <th>Document</th> */}
                            <th>Response</th>
                            <th>Date</th>
                            <th>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {reminders.map((reminder, reminderIndex) => (
                            <tr
                              key={
                                reminder.reminder_id ||
                                reminder.id ||
                                reminderIndex
                              }
                            >
                              <td>{reminder.office_or_stage || "-"}</td>

                              <td>
                                {reminder.reminder_date
                                  ? formatDate(reminder.reminder_date)
                                  : "-"}
                              </td>

                              <td className="text-wrap">
                                {reminder.remarks || reminder.message || "-"}
                              </td>

                              <td className="text-wrap">
                                {reminder.response_remarks || "-"}
                              </td>
                              <td>
                                {reminder.response_date?.slice(0, 10) || "-"}
                              </td>
                              {/* EDIT REMINDER */}
                              <td>
                                {isLatest && (
                                  <div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        onReminder(history, reminder)
                                      }
                                    >
                                      Update
                                    </button>{" "}
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() =>
                                        HandleDelete("reminder", reminder)
                                      }
                                    >
                                      <MdDeleteForever />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ==================================================
                    REMARKS
                ================================================== */}

                {history.remarks && (
                  <div className="mt-3">
                    <div className="small fw-semibold text-muted">Remarks</div>

                    <div>{history.remarks}</div>
                  </div>
                )}

                {/* ==================================================
                    EDIT LOCK
                ================================================== */}

                {isLatest && !editable && (
                  <div className="small text-danger mt-2">
                    🔒 Editing is available only for the latest history within 3
                    days of creation.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ========================================================
          PDF VIEWER MODAL
      ======================================================== */}

      {showPdfViewer && selectedPdf && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: "rgba(0,0,0,0.75)",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              {/* ==================================================
                  PDF HEADER
              ================================================== */}

              <div className="modal-header">
                <div>
                  <h5 className="modal-title fw-bold">
                    {selectedPdf.original_file_name || "PDF Document"}
                  </h5>

                  <div className="small text-muted">
                    {selectedPdf.document_type || "Document"}
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={closePdf}
                />
              </div>

              {/* ==================================================
                  PDF BODY
              ================================================== */}

              <div
                className="modal-body d-flex justify-content-center p-0"
                style={{
                  minHeight: "70vh",
                  height: "70vh",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                  }}
                >
                  <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <Viewer
                        fileUrl={selectedPdf.download_url}
                        plugins={[defaultLayoutPluginInstance]}
                        defaultScale={1}
                        onDocumentLoad={() => {
                          // console.log("PDF loaded successfully");
                        }}
                        onDocumentError={(error) => {
                          console.error("PDF Loading Error:", error);
                        }}
                      />
                    </div>
                  </Worker>
                </div>
              </div>

              {/* ==================================================
                  FOOTER
              ================================================== */}

              <div className="modal-footer">
                <a
                  href={selectedPdf.download_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-primary"
                >
                  Open in New Tab
                </a>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closePdf}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="p-3">
        {result?.success === true ? (
          <SuccessMsg
            head="Success"
            message={result.message}
            show={show}
            setShow={setShow}
          />
        ) : result ? (
          <FailedMsg
            head="Failed"
            message={result.message}
            show={show}
            setShow={setShow}
          />
        ) : null}
      </div>
    </>
  );
};

export default HistoryList;
